/**
 * SEO for the marketing site (www.thepolishedversion.com): per-page titles and
 * descriptions, schema.org JSON-LD, the crawler-readable copy of each page,
 * sitemap.xml and llms.txt.
 *
 * The plan prices and FAQ answers live HERE and the pages import them
 * (MarketingPricing, MarketingHome, MarketingFAQ), so the structured data and
 * llms.txt can never disagree with what the page shows. The other prose below
 * is copied word for word from the pages; tests/ai-search-seo.spec.ts fails if
 * a page's wording changes and this copy isn't updated.
 *
 * KEEP THIS FILE DEPENDENCY-FREE (no imports) — the Vercel functions import it
 * as `../src/lib/marketingSeo.js` (see the note in localSeo.ts).
 */

/** The marketing site's canonical origin (the apex redirects here). */
export const MARKETING_ORIGIN = 'https://www.thepolishedversion.com';
export const MARKETING_NAME = 'The Polished Version';
export const MARKETING_EMAIL = 'hello@thepolishedversion.com';
/** The 1200x630 link-preview card for the sales site (businesses' own sites use their logo). */
export const MARKETING_SHARE_IMAGE = `${MARKETING_ORIGIN}/share-card.jpg`;
const OG_IMAGE = MARKETING_SHARE_IMAGE;
const LOGO = `${MARKETING_ORIGIN}/favicon.png`;

// ─── Shared page data (imported by the pages) ──────────────────────────────────

export interface Plan {
  name: 'Solo' | 'Team' | 'Business';
  monthlyPrice: number;
  annualPrice: number;
  tagline: string;
  features: { label: string; included: boolean }[];
}

export const PLANS: Plan[] = [
  {
    name: 'Solo',
    monthlyPrice: 9,
    annualPrice: 99,
    tagline: 'For solo cleaners ready to look the part online.',
    features: [
      { label: 'Your own booking website', included: true },
      { label: 'Built for Google and AI search', included: true },
      { label: 'Your Google reviews on your site', included: true },
      { label: 'Online bookings and quotes', included: true },
      { label: 'Edit your site yourself', included: true },
      { label: 'Up to 1 team member', included: true },
      { label: 'Connect a domain you own', included: false },
      { label: 'Logins for your cleaners', included: false },
      { label: 'Text message reminders (connect your own Twilio account)', included: false },
      { label: 'AI logo maker', included: false },
      { label: 'AI proposal writer', included: false },
      { label: 'Remove “Powered by” from your footer', included: false },
      { label: 'Priority support', included: false },
    ],
  },
  {
    name: 'Team',
    monthlyPrice: 18,
    annualPrice: 180,
    tagline: 'For growing teams who want their own brand and domain.',
    features: [
      { label: 'Your own booking website', included: true },
      { label: 'Built for Google and AI search', included: true },
      { label: 'Your Google reviews on your site', included: true },
      { label: 'Online bookings and quotes', included: true },
      { label: 'Edit your site yourself', included: true },
      { label: 'Up to 5 team members', included: true },
      { label: 'Connect a domain you own', included: true },
      { label: 'Logins for your cleaners', included: true },
      { label: 'Text message reminders (connect your own Twilio account)', included: true },
      { label: 'AI logo maker', included: true },
      { label: 'AI proposal writer', included: false },
      { label: 'Remove “Powered by” from your footer', included: false },
      { label: 'Priority support', included: false },
    ],
  },
  {
    name: 'Business',
    monthlyPrice: 25,
    annualPrice: 250,
    tagline: 'For busy companies with bigger teams and fuller diaries.',
    features: [
      { label: 'Your own booking website', included: true },
      { label: 'Built for Google and AI search', included: true },
      { label: 'Your Google reviews on your site', included: true },
      { label: 'Online bookings and quotes', included: true },
      { label: 'Edit your site yourself', included: true },
      { label: 'Unlimited team members', included: true },
      { label: 'Connect a domain you own', included: true },
      { label: 'Logins for your cleaners', included: true },
      { label: 'Text message reminders (connect your own Twilio account)', included: true },
      { label: 'AI logo maker', included: true },
      { label: 'AI proposal writer', included: true },
      { label: 'Remove “Powered by” from your footer', included: true },
      { label: 'Priority support', included: true },
    ],
  },
];

export const planByName = (name: Plan['name']): Plan => PLANS.find((p) => p.name === name) as Plan;

export interface Faq { q: string; a: string }
export interface FaqGroup { id: string; title: string; items: Faq[] }

/** The /faq page's questions (icons and colours stay in MarketingFAQ.tsx). */
export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    items: [
      {
        q: 'How fast can I be live?',
        a: 'About 60 seconds. Sign up, pick your business name and your booking site is live at yourcompany.thepolishedversion.com straight away. Tweak everything else from your admin whenever suits you.',
      },
      {
        q: 'Do I need a designer or a developer?',
        a: 'Not at all. The visual editor lets you change words, photos, services and prices without touching a line of code. If you can edit a Word document, you can run your website.',
      },
      {
        q: 'Will customers find me on Google and in AI answers like ChatGPT?',
        a: 'Your site is built for it from day one. Every page gets its own title and description, there’s a sitemap, and your services, prices, areas and opening hours are spelled out behind the scenes in the format Google, Bing and AI assistants like ChatGPT, Perplexity and Claude read. Search engines and AI tools are welcome to look round, and it all loads fast on a phone. Nobody can honestly promise you the top spot, but you start with everything in place, and your Google reviews help win people over once they land.',
      },
      {
        q: 'What happens after the 30-day trial?',
        a: 'If you’re happy, choose a plan and add your card through Stripe. You won’t be charged until your 30 free days are up. If it’s not for you, don’t choose a plan and you won’t pay a penny.',
      },
    ],
  },
  {
    id: 'domains-branding',
    title: 'Domains and branding',
    items: [
      {
        q: 'Can I use my own domain?',
        a: 'Yes, on Team and Business you can connect a domain you already own: .co.uk, .com or anything else. The domain itself isn’t included; you buy and renew it with any domain provider. Type it into your admin, add the two records we show you at your provider, and we set up the secure connection. Every plan also comes with a free yourcompany.thepolishedversion.com address.',
      },
      {
        q: 'Can I match my brand colours and logo?',
        a: 'Yes. Upload your logo, pick your main colour and choose a theme. No logo yet? On Team and Business, the AI logo maker can design one for you. Your website and booking pages follow your brand, and emails to customers go out under your business name.',
      },
      {
        q: 'Can you make me a logo?',
        a: 'Yes, on Team and Business. The AI logo maker designs three logos from your business name, a style and your colours, in seconds. Pick the one you like and it goes straight onto your website, or have another go. You get two goes every 24 hours.',
      },
    ],
  },
  {
    id: 'bookings-payments',
    title: 'Bookings and payments',
    items: [
      {
        q: 'How do customers pay?',
        a: 'Card customers pay when they book, either in full or a deposit, whichever you choose. That way a no-show doesn’t cost you. You can also accept cash on the day or PayPal if you’re happy to. Quote jobs and balances get an invoice with a Pay now button.',
      },
      {
        q: 'Can AI write my quotes?',
        a: 'Yes, on Business. When a customer asks for a quote, type a few notes about the job and the AI proposal writer drafts the quote, priced from your own services. You check it and change anything you like before it goes to the customer.',
      },
      {
        q: 'Do you charge per booking?',
        a: 'No. Your monthly plan is your monthly plan. We never take a cut of your jobs.',
      },
      {
        q: 'Can my team members log in?',
        a: 'Yes. Team and Business plans let you invite staff with their own login: owner, supervisor or cleaner. Cleaners only see their own schedule.',
      },
    ],
  },
  {
    id: 'switching-data',
    title: 'Switching and data',
    items: [
      {
        q: 'I already have a website. Can I switch?',
        a: 'Absolutely. Most teams keep their existing domain and point it at their new site. We can help bring across your services, prices and basic content.',
      },
      {
        q: 'Who owns my data?',
        a: 'You do. Export your bookings and customer list any time. We never resell or repurpose your data.',
      },
      {
        q: 'Can I cancel?',
        a: 'Yes, any time from your admin. No contract, no exit fee, no sales call.',
      },
    ],
  },
];

/** The questions at the bottom of /pricing. */
export const PRICING_FAQS: Faq[] = [
  {
    q: 'When do I start paying?',
    a: 'Your first 30 days are free. When you’re ready, choose a plan and add your card through Stripe, and you won’t be charged until the free month is up. If it’s not for you, don’t choose a plan and you won’t pay a penny.',
  },
  {
    q: 'Do you take a cut of my bookings?',
    a: 'Never. You pay one flat monthly fee and we don’t take anything from what your customers pay you. If they pay by card, Stripe charges its usual fee.',
  },
  {
    q: 'Can I use my own domain?',
    a: 'Yes, on Team and Business you can connect a domain you already own: .co.uk, .com or anything else. The domain itself isn’t included; you buy and renew it with any domain provider. Type it into your admin, add the two records we show you at your provider, and we set up the secure connection. Every plan also comes with a free yourcompany.thepolishedversion.com address.',
  },
  {
    q: 'What if I want to leave?',
    a: 'Cancel any time from your admin. No contract, no exit fee and no sales call trying to keep you. Download your bookings and customer list whenever you like.',
  },
];

// ─── Page copy for crawlers ────────────────────────────────────────────────────

type Section = { h2: string; paras?: string[]; items?: string[]; faqs?: Faq[]; plans?: boolean };

export interface MarketingPage {
  path: string;
  /** Source file the copy below is taken from (checked by the tests). */
  source?: string;
  title: string;
  description: string;
  /** Breadcrumb / nav name. */
  label: string;
  h1?: string;
  lede?: string;
  sections?: Section[];
  /** Pages without crawler copy (legal) still get the meta + JSON-LD. */
  inSitemap: boolean;
}

export const MARKETING_PAGES: MarketingPage[] = [
  {
    path: '/',
    source: 'src/pages/MarketingHome.tsx',
    label: 'Home',
    title: 'The Polished Version | Websites & Booking for Cleaners',
    description:
      'Websites and booking software for cleaning businesses: a site built for Google and AI search, online bookings and payments, and your team’s schedule.',
    h1: 'Build your website. Manage your bookings. Grow your business.',
    lede:
      'Everything you need to run and grow your cleaning business, all in one place. Build a professional, SEO and AI optimised website to attract customers, take online bookings, manage jobs and schedules, and keep your business running smoothly. All with zero booking commissions.',
    sections: [
      {
        h2: 'You didn’t start a cleaning business to do admin at 9pm.',
        paras: [
          'Last clean done, feet up at last, and the phone goes again: “Any chance of Thursday?” Let your website answer that one. Customers see your free slots and book themselves in, and you get your evening back.',
        ],
      },
      {
        h2: 'From a Google search to a paid job.',
        paras: [
          'You’re in complete control of your services, pricing and availability. Customers can book and pay online, with the option to pay in full or leave a deposit. Choose whether bookings are confirmed automatically or require your approval. Once confirmed, assign the job yourself or let the system automatically allocate an available cleaner. From booking to job completion, everything is organised and managed in one place.',
        ],
      },
      {
        h2: 'Found, booked and sorted.',
        paras: [
          'Most cleaning businesses run on a website, a booking app and a group chat, and hope nothing slips through the gaps. Here it’s all one thing. The booking made on your site is the same booking on your schedule, on your cleaner’s phone and on the invoice.',
        ],
        items: [
          'Get found: Someone types “end of tenancy clean Cardiff” into Google, or asks ChatGPT who’s good near them. Your site is set up to be found for exactly that, and when they land, your Google star rating and reviews are right there to win them over.',
          'Get booked: You decide how each service is sold. Give it a price and customers book a free slot in under a minute, even at 11pm on a Sunday. Make it quote only and they send a request, you price it, and they accept online.',
          'Get it done: Every booking lands on one schedule. Put a cleaner on it and it appears in their own login. Invoices, payments and how the month is going are all in the same place.',
          'New: AI tools. The AI logo maker designs you a logo in seconds (Team and Business), and the AI proposal writer turns a few notes about a job into a priced quote (Business).',
        ],
      },
      {
        h2: 'The back office, minus the office.',
        items: [
          'New bookings and quote requests waiting in one place',
          'Your services, prices and how long each clean takes',
          'Regulars on repeat: weekly, fortnightly or monthly',
          'A login for each cleaner, showing only their jobs',
          'Invoices your customers can pay by card or PayPal',
          'How the month’s going, at a glance',
        ],
      },
      {
        h2: 'Simple pricing',
        paras: ['Plans start at £9. No setup fee, no contract, and we never take a slice of what your customers pay you.'],
        plans: true,
      },
    ],
    inSitemap: true,
  },
  {
    path: '/features',
    source: 'src/pages/MarketingFeatures.tsx',
    label: 'Features',
    title: 'Features for Cleaning Businesses | The Polished Version',
    description:
      'A website built for Google and AI search, online bookings and quotes, one schedule for your team, and card, PayPal or cash payments. See every feature.',
    h1: 'Everything it takes to fill your diary.',
    lede: 'Get found on Google and in AI search, take bookings while you sleep, send the right cleaner to the right door and get paid on the day. Here’s how.',
    sections: [
      {
        h2: 'Your website: look like the obvious choice.',
        paras: [
          'Get discovered. Make a great first impression. Win more customers. Your professional website is designed to help local customers find you through Google, Bing and AI search tools like ChatGPT. Fast, mobile-friendly and built to turn visitors into bookings, with your services and prices clearly displayed. And when you need to update something? Change your prices, photos or content in just a few clicks, without paying a web designer.',
        ],
        items: [
          'Search-ready from day one: page titles, descriptions, a sitemap and the areas you cover',
          'Your services, prices, areas and hours spelled out for Google and AI assistants like ChatGPT',
          'Themes made for cleaning companies, in your logo and colours',
          'No logo yet? Design one with AI on Team and Business',
          'Change any page, photo or price yourself',
          'Your Google star rating and latest reviews, pulled onto your site live',
          'Connect a domain you already own on Team and Business',
        ],
      },
      {
        h2: 'Bookings & quotes: bookings that land while you sleep.',
        paras: [
          'The first cleaner to reply often gets the job. What if you didn’t need to reply at all? Customers can check your availability, choose a service and book online, 24/7. Offer instant bookings at fixed prices or let customers request and accept personalised quotes. You control whether bookings are confirmed automatically or need your approval. Less waiting for customers, fewer missed opportunities for you.',
        ],
        items: [
          'Live free slots, so nobody books a time you’re busy',
          'Confirm bookings instantly, or approve each one yourself. Your call.',
          'Each service is fixed price (book straight away) or quote only (request, you price it, they accept)',
          'Regulars booked weekly, fortnightly, 4-weekly or monthly',
          'Reminder emails, so customers remember you’re coming (and text message reminders on Team and Business, through your own Twilio account)',
          'Every enquiry in one inbox, so none slip through the cracks',
          'On Business, type a few notes about a job and AI drafts the quote for you to check',
        ],
      },
      {
        h2: 'Schedule & team: everyone knows where to be.',
        paras: [
          'No more Sunday-night texts working out who’s going where. Every job sits on one schedule. Put a cleaner on it and it shows up in their own login, with their jobs and nobody else’s.',
        ],
        items: [
          'The whole team’s week on one screen',
          'Move a job to someone else when a cleaner’s off sick',
          'Supervisors who can run the diary for you',
          'Cleaners only ever see their own jobs',
          'Jobs sync to the calendar on their phone',
        ],
      },
      {
        h2: 'Payments & admin: get paid on the day, not next month.',
        paras: [
          'Card customers pay when they book, in full or a deposit, so a no-show doesn’t cost you. Happy to take cash on the day? Offer that too, it’s your call. Invoices for anything else go out with a link to pay online, and if you ever leave, your data comes with you.',
        ],
        items: [
          'Card payments through Stripe, PayPal or good old cash',
          'We take nothing from your jobs (Stripe’s usual card fee applies)',
          'Choose full payment or a deposit when customers book by card',
          'Download your bookings and customers whenever you like',
        ],
      },
      {
        h2: 'AI tools: a logo and a quote, done in seconds.',
        paras: [
          'Two AI helpers are built in, for the jobs that usually eat an evening. You stay in charge: you pick the logo, and you check every quote before it goes.',
        ],
        items: [
          'AI logo maker: No logo yet? Type in your business name, pick a style and your colours, and get three designs to choose from in seconds. Pick the one you like and it’s your logo, on a clean see-through background, ready for your website. Not quite right? Have another go. On Team and Business',
          'AI proposal writer: A customer asks for a quote. Instead of starting from a blank page, jot down the job in your own words and AI drafts the quote for you, priced from your own services. Check it, change anything you like, then send it. On Business',
        ],
      },
      {
        h2: 'And the rest',
        items: [
          'Connect your domain: Already own a domain? Connect it on Team and Business and we set up the secure connection for you.',
          'Your brand, everywhere: Your logo and colours, from the homepage right through to booking.',
          'Extra pages: Add a page for each town you cover, or a careers page, in minutes.',
          'Google reviews: Connect your Google listing once and your star rating and latest reviews show on your site, always up to date.',
          'Calendar sync: Jobs show up in Google, Apple or Outlook calendar.',
          'Made for phones: Most people book from the sofa on their phone. It’s built for that.',
          'Email and text reminders: Customers get a reminder before you’re due, so fewer wasted trips. Text reminders are on Team and Business: connect your own Twilio account and Twilio bills you for the texts.',
          'Insights: Which cleans earn the most, and who still owes you.',
        ],
      },
    ],
    inSitemap: true,
  },
  {
    path: '/pricing',
    source: 'src/pages/MarketingPricing.tsx',
    label: 'Pricing',
    title: 'Pricing: Plans from £9 a Month | The Polished Version',
    description:
      'Solo £9, Team £18 and Business £25 a month. 30 days free, no setup fee, no contract and no commission on your bookings. Cancel any time.',
    h1: 'Pays for itself in one clean.',
    lede: 'One flat monthly fee. No setup costs, no commission, no contract. Try it free for 30 days and only stay if it’s winning you work.',
    sections: [
      { h2: 'Plans', plans: true },
      {
        h2: 'Every plan includes',
        items: ['Live in 60 seconds', '30 days free', 'Zero commission', 'Take card, PayPal or cash', 'Export your data any time', 'Cancel any time'],
      },
      { h2: 'Pricing questions', faqs: PRICING_FAQS },
    ],
    inSitemap: true,
  },
  {
    path: '/faq',
    source: 'src/pages/MarketingFAQ.tsx',
    label: 'FAQ',
    title: 'FAQ: Booking Software for Cleaners | The Polished Version',
    description:
      'Straight answers to what cleaning company owners ask before they sign up: going live, domains, payments, team logins, switching and your data.',
    h1: 'Straight answers. No small print.',
    lede: 'Everything cleaning company owners ask us before they sign up. If yours isn’t here, just ask.',
    sections: FAQ_GROUPS.map((g) => ({ h2: g.title, faqs: g.items })),
    inSitemap: true,
  },
  {
    path: '/about',
    source: 'src/pages/MarketingAbout.tsx',
    label: 'About',
    title: 'About Us | The Polished Version',
    description:
      'Small cleaning companies deserve the same polish as the big franchises, without the big-franchise budget. That’s the idea behind The Polished Version.',
    h1: 'Made for the people who do the cleaning.',
    lede: 'Small cleaning companies deserve the same polish as the big franchises, without the big-franchise budget. That’s the whole idea.',
    sections: [
      {
        h2: 'One cleaning website that actually worked.',
        paras: [
          'The Polished Version began as the website for a single cleaning company. It sold the service, took the bookings, handled the quotes and kept the team’s week in order.',
          'So we turned it into a platform any cleaning company can use. Same polish, same booking flow, same schedule. Yours in 60 seconds, with your name on it.',
        ],
      },
      {
        h2: 'Three rules we don’t break.',
        items: [
          'Professional should be easy. You shouldn’t need an agency, a developer or a nephew who “does websites” to look credible online. Pick a template, add your logo, go live.',
          'The website should do the work. A pretty brochure that sends you emails isn’t enough. Bookings, quotes, services, team and schedule belong in one joined-up system.',
          'Built for people on the tools. Owners, supervisors and cleaners need clarity, not complicated software. Everyone sees what they need and nothing they don’t.',
        ],
      },
      {
        h2: 'Our promises',
        items: [
          'No commission. Ever. A flat monthly fee. We never take a slice of your jobs.',
          'Try it properly first. 30 days free to build, take bookings and decide for yourself.',
          'No lock-in. Cancel any time. No contract, no exit fee, no sales call.',
          'Your data stays yours. Export bookings and customers any time. We never resell it.',
        ],
      },
    ],
    inSitemap: true,
  },
  {
    path: '/sales-contact',
    source: 'src/pages/SalesContact.tsx',
    label: 'Contact',
    title: 'Contact Us | The Polished Version',
    description:
      'Questions about plans, domains, moving from your old site or setting up your team? Ask The Polished Version. No sales script, just straight answers.',
    h1: 'Got questions? Let’s talk.',
    lede: 'Plans, domains, moving from your old site or setting up your team. Ask away. No sales script, just straight answers.',
    sections: [{ h2: 'Rather just email?', paras: [MARKETING_EMAIL] }],
    inSitemap: true,
  },
  {
    path: '/privacy',
    label: 'Privacy Policy',
    title: 'Privacy Policy | The Polished Version',
    description: 'How The Polished Version collects, uses and protects personal data, and the rights you have over it.',
    inSitemap: true,
  },
  {
    path: '/terms',
    label: 'Terms and Conditions',
    title: 'Terms and Conditions | The Polished Version',
    description: 'The terms that apply when you use The Polished Version website and booking software for cleaning businesses.',
    inSitemap: true,
  },
  {
    path: '/cookies',
    label: 'Cookie Policy',
    title: 'Cookie Policy | The Polished Version',
    description: 'Which cookies and similar technologies The Polished Version uses, and how to control them.',
    inSitemap: true,
  },
];

const normalizePath = (path: string) =>
  `/${String(path ?? '').split(/[?#]/)[0]}`.replace(/\/+/g, '/').replace(/(.)\/$/, '$1').toLowerCase();

export function marketingPageFor(path: string): MarketingPage | null {
  const p = normalizePath(path);
  return MARKETING_PAGES.find((page) => page.path === p) ?? null;
}

export const marketingUrl = (path: string) => `${MARKETING_ORIGIN}${path === '/' ? '/' : path}`;

// ─── JSON-LD ───────────────────────────────────────────────────────────────────

const ORG_ID = `${MARKETING_ORIGIN}/#organization`;
const SITE_ID = `${MARKETING_ORIGIN}/#website`;
const APP_ID = `${MARKETING_ORIGIN}/#software`;

function planOffers(): Record<string, unknown>[] {
  return PLANS.flatMap((plan) => [
    { plan, period: 'month' as const, price: plan.monthlyPrice, unitCode: 'MON' },
    { plan, period: 'year' as const, price: plan.annualPrice, unitCode: 'ANN' },
  ]).map(({ plan, period, price, unitCode }) => ({
    '@type': 'Offer',
    name: `${plan.name} (${period === 'month' ? 'monthly' : 'annual'})`,
    description: plan.tagline,
    price: price.toFixed(2),
    priceCurrency: 'GBP',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: price.toFixed(2),
      priceCurrency: 'GBP',
      unitCode,
      unitText: period,
    },
    url: marketingUrl('/pricing'),
  }));
}

function faqPage(url: string, faqs: Faq[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${url}#faq`,
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };
}

/**
 * JSON-LD blocks for a marketing page. Every page: Organization + WebSite.
 * Home & pricing: SoftwareApplication with the real plan prices. FAQ & pricing:
 * FAQPage (the questions are on the page). Others: BreadcrumbList. No ratings —
 * we don't have independent review data to mark up.
 */
export function buildMarketingJsonLd(page: MarketingPage): Record<string, unknown>[] {
  const url = marketingUrl(page.path);
  const blocks: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': ORG_ID,
      name: MARKETING_NAME,
      url: `${MARKETING_ORIGIN}/`,
      logo: LOGO,
      email: MARKETING_EMAIL,
      contactPoint: { '@type': 'ContactPoint', contactType: 'sales', email: MARKETING_EMAIL, url: marketingUrl('/sales-contact') },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': SITE_ID,
      name: MARKETING_NAME,
      url: `${MARKETING_ORIGIN}/`,
      inLanguage: 'en-GB',
      publisher: { '@id': ORG_ID },
    },
  ];
  if (page.path === '/' || page.path === '/pricing') {
    const prices = PLANS.map((p) => p.monthlyPrice);
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': APP_ID,
      name: MARKETING_NAME,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: `${MARKETING_ORIGIN}/`,
      image: OG_IMAGE,
      description:
        'Website, online booking, scheduling, team and payment software for cleaning businesses. 30 days free, no setup fee, no contract and no commission on bookings.',
      publisher: { '@id': ORG_ID },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'GBP',
        lowPrice: Math.min(...prices).toFixed(2),
        highPrice: Math.max(...prices).toFixed(2),
        offerCount: PLANS.length,
        offers: planOffers(),
      },
    });
  }
  if (page.path === '/faq') blocks.push(faqPage(url, FAQ_GROUPS.flatMap((g) => g.items)));
  if (page.path === '/pricing') blocks.push(faqPage(url, PRICING_FAQS));
  if (page.path !== '/') {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${MARKETING_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: page.label, item: url },
      ],
    });
  }
  return blocks;
}

// ─── Crawler-readable HTML ─────────────────────────────────────────────────────

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

const NAV: Array<[string, string]> = [
  ['/', 'Home'],
  ['/features', 'Features'],
  ['/pricing', 'Pricing'],
  ['/about', 'About'],
  ['/faq', 'FAQ'],
  ['/demo', 'Live demo'],
  ['/sales-contact', 'Contact'],
  ['/signup', 'Free trial'],
];

function plansHtml(): string {
  return `<ul>${PLANS.map((p) => {
    const saving = p.monthlyPrice * 12 - p.annualPrice;
    const included = p.features.filter((f) => f.included).map((f) => `<li>${esc(f.label)}</li>`).join('');
    return `<li><h3>${esc(p.name)}: £${p.monthlyPrice} a month</h3><p>${esc(p.tagline)}</p><p>Or £${p.annualPrice} a year (save £${saving}).</p><ul>${included}</ul></li>`;
  }).join('')}</ul><p>30 days free on every plan. <a href="/signup">Start your free trial</a>.</p>`;
}

/**
 * The page's copy as plain semantic HTML for crawlers that don't run
 * JavaScript. Same words as the page (see the module comment); index.html hides
 * it while JavaScript runs and React replaces it on mount. null for pages with
 * no crawler copy (legal pages).
 */
export function buildMarketingPrerenderHtml(page: MarketingPage): string | null {
  if (!page.h1) return null;
  const sections = (page.sections ?? []).map((s) => {
    const parts = [`<h2>${esc(s.h2)}</h2>`];
    for (const p of s.paras ?? []) parts.push(`<p>${esc(p)}</p>`);
    if (s.items?.length) parts.push(`<ul>${s.items.map((i) => `<li>${esc(i)}</li>`).join('')}</ul>`);
    if (s.faqs?.length) parts.push(`<dl>${s.faqs.map((f) => `<dt>${esc(f.q)}</dt><dd>${esc(f.a)}</dd>`).join('')}</dl>`);
    if (s.plans) parts.push(plansHtml());
    return `<section>${parts.join('')}</section>`;
  });
  const nav = NAV.map(([href, label]) => `<a href="${href}">${esc(label)}</a>`).join(' ');
  return [
    '<div data-tpv-prerender="marketing">',
    `<header><p><a href="/">${MARKETING_NAME}</a></p><nav aria-label="Main">${nav}</nav></header>`,
    `<main><h1>${esc(page.h1)}</h1>${page.lede ? `<p>${esc(page.lede)}</p>` : ''}${sections.join('')}</main>`,
    `<footer><p>${MARKETING_NAME}: websites and booking software for cleaning businesses. <a href="mailto:${MARKETING_EMAIL}">${MARKETING_EMAIL}</a></p>`,
    '<p><a href="/privacy">Privacy Policy</a> <a href="/terms">Terms and Conditions</a> <a href="/cookies">Cookie Policy</a></p></footer>',
    '</div>',
  ].join('');
}

// ─── sitemap.xml & llms.txt ────────────────────────────────────────────────────

export function buildMarketingSitemapXml(): string {
  const urls = MARKETING_PAGES.filter((p) => p.inSitemap)
    .map((p) => `  <url>\n    <loc>${esc(marketingUrl(p.path))}</loc>\n  </url>`)
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}

export function buildMarketingLlmsTxt(): string {
  const page = (path: string) => marketingPageFor(path) as MarketingPage;
  const plans = PLANS.map((p) => {
    const extras = p.features.filter((f) => f.included).map((f) => f.label).join(', ');
    return `- ${p.name}: £${p.monthlyPrice} a month or £${p.annualPrice} a year. ${p.tagline} Includes: ${extras}.`;
  });
  const faqs = [...FAQ_GROUPS.flatMap((g) => g.items), ...PRICING_FAQS.filter((f) => !FAQ_GROUPS.some((g) => g.items.some((i) => i.q === f.q)))];
  return [
    `# ${MARKETING_NAME}`,
    '',
    '> Website, online booking, scheduling, team and payment software for cleaning businesses. Every cleaning company gets its own booking website, set up to be found on Google and in AI search, on a free yourcompany.thepolishedversion.com address or (Team and Business plans) a domain they already own. Plans from £9 a month, 30 days free, no setup fee, no contract and no commission on bookings.',
    '',
    `- Website: ${MARKETING_ORIGIN}/`,
    `- Email: ${MARKETING_EMAIL}`,
    `- Free trial: ${marketingUrl('/signup')}`,
    `- Live demo site: ${marketingUrl('/demo')}`,
    '',
    '## Plans',
    '',
    ...plans,
    '- Every plan: 30 days free, live in 60 seconds, zero commission, take card, PayPal or cash, export your data any time, cancel any time.',
    '- AI tools: an AI logo maker on Team and Business (three designs a go from your business name, style and colours) and an AI proposal writer on Business (drafts a quote from your notes, priced from your own services, for you to check before it goes).',
    '',
    '## Pages',
    '',
    `- [Features](${marketingUrl('/features')}): ${page('/features').description}`,
    `- [Pricing](${marketingUrl('/pricing')}): ${page('/pricing').description}`,
    `- [FAQ](${marketingUrl('/faq')}): ${page('/faq').description}`,
    `- [About](${marketingUrl('/about')}): ${page('/about').description}`,
    `- [Contact](${marketingUrl('/sales-contact')}): ${page('/sales-contact').description}`,
    '',
    '## Questions and answers',
    '',
    ...faqs.map((f) => `- ${f.q} ${f.a}`),
    '',
    '## Optional',
    '',
    `- [Privacy Policy](${marketingUrl('/privacy')})`,
    `- [Terms and Conditions](${marketingUrl('/terms')})`,
    `- [Cookie Policy](${marketingUrl('/cookies')})`,
    '',
  ].join('\n');
}
